import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegister, RegisterData } from '../hooks/useRegister';
import { toast } from 'react-toastify';
import { Eye, EyeOff, Loader2, Mail } from 'lucide-react';

interface UserRegistrationProps {
  onSwitchToLogin?: () => void;
  setShowLogin?: (show: boolean) => void;
}

const UserRegistration: React.FC<UserRegistrationProps> = ({ onSwitchToLogin, setShowLogin }) => {
  const navigate = useNavigate();
  const { register, loading } = useRegister();
  const [showPassword, setShowPassword] = useState(false);
  const [registeredPendingConfirmation, setRegisteredPendingConfirmation] = useState(false);
  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    password: '',
    name: '',
    client_type: 'small',
  });

  const isInsideModal = Boolean(onSwitchToLogin || setShowLogin);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    const result = await register(formData);
    
    if (result.success) {
      if (result.needsConfirmation) {
        setRegisteredPendingConfirmation(true);
        toast.info('¡Cuenta creada! Revisa tu correo electrónico para confirmar tu cuenta.');
      } else {
        toast.success('¡Registro e inicio de sesión exitoso!');
        if (setShowLogin) setShowLogin(false);
        navigate('/perfil');
      }
    } else {
      if (result.error?.includes('already registered') || result.error?.includes('User already registered')) {
        toast.error('Este correo ya se encuentra registrado. Intenta iniciar sesión.');
      } else {
        toast.error(result.error || 'Error en el registro');
      }
    }
  };

  const renderSuccessState = () => (
    <div className="w-full text-center py-4 space-y-5">
      <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto shadow-xs">
        <Mail className="w-8 h-8" />
      </div>

      <div className="space-y-2">
        <h3 className="text-xl font-bold text-gray-900">¡Cuenta creada con éxito!</h3>
        <p className="text-sm text-gray-600 max-w-sm mx-auto">
          Hemos enviado un enlace de confirmación a <strong className="text-gray-800">{formData.email}</strong>.
        </p>
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mt-3">
          Por favor revisa tu bandeja de entrada (y la carpeta de spam) y haz clic en el enlace para activar tu cuenta.
        </p>
      </div>

      <div className="pt-4 flex flex-col gap-2">
        {onSwitchToLogin ? (
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm"
          >
            Ir a Iniciar Sesión
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm"
          >
            Volver al Inicio
          </button>
        )}
      </div>
    </div>
  );

  const formBody = (
    <div className="w-full max-w-md">
      {registeredPendingConfirmation ? (
        renderSuccessState()
      ) : (
        <>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Nombre Completo / Contacto *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej. Juan Pérez"
                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                required
              />
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Correo Electrónico *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="tu@correo.com"
                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Contraseña (mínimo 6 caracteres) *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  minLength={6}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent pr-10 transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Tipo de Cliente / Empresa
              </label>
              <select
                value={formData.client_type}
                onChange={(e) => setFormData({ ...formData, client_type: e.target.value as any })}
                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all bg-white"
                required
              >
                <option value="small">Pequeña Empresa / Particular</option>
                <option value="medium">Mediana Empresa</option>
                <option value="large">Gran Empresa / Corporativo</option>
                <option value="distributor">Distribuidor Mayorista</option>
              </select>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="animate-spin h-4 w-4 text-white" />
                    <span>Creando cuenta...</span>
                  </span>
                ) : (
                  'Registrarse'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-gray-500">
                  ¿Ya tienes una cuenta?
                </span>
              </div>
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  if (onSwitchToLogin) {
                    onSwitchToLogin();
                  } else {
                    navigate('/');
                  }
                }}
                className="w-full flex justify-center py-2.5 px-4 border border-amber-500 text-amber-600 rounded-xl text-sm font-semibold hover:bg-amber-50 transition-colors"
              >
                Iniciar Sesión
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (isInsideModal) {
    return formBody;
  }

  return (
    <div className="min-h-[calc(100vh-76px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50/50">
      <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Crear Cuenta</h2>
          <p className="mt-1 text-sm text-gray-600">Completa tus datos para registrarte</p>
        </div>
        {formBody}
      </div>
    </div>
  );
};

export default UserRegistration;
